/**
 * Symptom-group taxonomy — the 8 lived-experience groups the `/symptoms`
 * viewer is organised by. Mirrors the `Category` shape in `lib/categories.ts`.
 *
 * These are STRUCTURAL FALLBACKS. The CMS `symptom-groups` collection is the
 * source of truth for the localized name/description and (optionally) an
 * uploaded icon + color. Consumers prefer the CMS value and fall back to the
 * entry here for the non-localized bits the CMS may not own (icon, accent
 * colours) and as a last-resort English label.
 *
 * Transcribed from `docs/superpowers/specs/2026-06-16-symptoms-taxonomy-reference.md`
 * (Groups table). Order in this array is the canonical display order in the
 * viewer's group-chip row.
 */

export interface SymptomGroupMeta {
  /** Stable id (== slug == CMS `symptom-groups` slug). */
  id: string
  /** English fallback label. CMS name wins when present. */
  label: string
  slug: string
  /** Lucide icon name — maps to a component in `lib/category-icons.tsx`. */
  icon: string
  /** Tailwind background accent, e.g. `bg-amber-50`. */
  color: string
  /** Tailwind text accent, e.g. `text-amber-700`. */
  textColor: string
}

export const SYMPTOM_GROUPS: SymptomGroupMeta[] = [
  {
    id: "energy-fatigue",
    label: "Energy & fatigue",
    slug: "energy-fatigue",
    icon: "battery-low",
    color: "bg-amber-50",
    textColor: "text-amber-700",
  },
  {
    id: "weight-metabolism",
    label: "Weight & metabolism",
    slug: "weight-metabolism",
    icon: "scale",
    color: "bg-lime-50",
    textColor: "text-lime-700",
  },
  {
    id: "mood-sleep-stress",
    label: "Mood, sleep & stress",
    slug: "mood-sleep-stress",
    icon: "brain",
    color: "bg-violet-50",
    textColor: "text-violet-700",
  },
  {
    id: "hormones-sexual-health",
    label: "Hormones & sexual health",
    slug: "hormones-sexual-health",
    icon: "venus-and-mars",
    color: "bg-pink-50",
    textColor: "text-pink-700",
  },
  {
    id: "digestion",
    label: "Digestion",
    slug: "digestion",
    icon: "salad",
    color: "bg-emerald-50",
    textColor: "text-emerald-700",
  },
  {
    id: "heart-circulation",
    label: "Heart & circulation",
    slug: "heart-circulation",
    icon: "heart-pulse",
    color: "bg-red-50",
    textColor: "text-red-700",
  },
  {
    id: "breathing",
    label: "Breathing & airways",
    slug: "breathing",
    icon: "wind",
    color: "bg-sky-50",
    textColor: "text-sky-700",
  },
  {
    id: "pain-bones-muscles",
    label: "Pain, bones & muscles",
    slug: "pain-bones-muscles",
    icon: "bone",
    color: "bg-orange-50",
    textColor: "text-orange-700",
  },
]

/**
 * Resolve a symptom-group slug to its fallback metadata (label + colours +
 * icon name). Returns `undefined` for an unknown slug so callers can decide
 * whether to render an extra group from the CMS that isn't in this list.
 */
export function getSymptomGroupMeta(
  slug: string
): SymptomGroupMeta | undefined {
  return SYMPTOM_GROUPS.find((g) => g.slug === slug)
}
